/**
 * #1880 — is een naam op een akte dezelfde persoon als een naam uit de boom?
 *
 * Archieven spellen namen wisselend: Elizabeth/Elisabeth, Arendse/Aarissen,
 * Geus/de Geus, Lijsbeth/Lysbeth. Deze vergelijking is gemeten op 60.829
 * akteregels van 66 personen (204 gekoppelde aktes, prod-replica 23-10).
 * Ruimer dan `vergelijkNaam` in de score: die bepaalt punten, deze alleen
 * of een bekende uit de boom op de akte staat.
 *
 * #1884 — gedeeld met de server (naamgenoten-blokken), zodat client en server
 * dezelfde regels gebruiken. De client importeert via src/utils/naamGelijkenis.ts.
 */

/**
 * Naam naar een vergelijkingssleutel: zonder accenten, tussenvoegsels en spellingvarianten.
 * @param {string | null | undefined} s
 * @returns {string}
 */
export function naamSleutel(s) {
  return (
    (s || '')
      .normalize('NFKD')
      .replace(/[^\x00-\x7f]/g, '')
      .toLowerCase()
      .replace(/\b(van|de|der|den|ter|ten|het)\b/g, ' ')
      .replace(/y/g, 'ij')
      .replace(/z/g, 's')
      .replace(/c/g, 'k')
      .replace(/th/g, 't')
      .replace(/ph/g, 'f')
      // Poort 24-10: Joannis = Johannes (h tussen klinkers), Aart = Aard (d aan het eind klinkt als t).
      .replace(/([aeiou])h(?=[aeiou])/g, '$1')
      .replace(/d\b/g, 't')
      .replace(/kk/g, 'k')
      .replace(/([a-z])\1/g, '$1')
      .replace(/\s+/g, ' ')
      .trim()
  );
}

/**
 * Lengte van de langste gemeenschappelijke deelreeks.
 * @param {string} a
 * @param {string} b
 * @returns {number}
 */
export function lcs(a, b) {
  const m = Array(b.length + 1).fill(0);
  for (const x of a) {
    let v = 0;
    for (let j = 1; j <= b.length; j++) {
      const t = m[j];
      m[j] = x === b[j - 1] ? v + 1 : Math.max(m[j], m[j - 1]);
      v = t;
    }
  }
  return m[b.length];
}

/**
 * @param {string} a
 * @param {string} b
 * @returns {number}
 */
export const gelijkenis = (a, b) => (2 * lcs(a, b)) / (a.length + b.length);

/**
 * Zelfde eerste voornaam (sleutels uit `naamSleutel`): hetzelfde begin, of dezelfde beginletter met een
 * kleine spellingsafwijking (Pietronella/Petronella, Tjibbe/Tjebbe). Klaas/Nicolaas blijft verschillend.
 * @param {string} x
 * @param {string} y
 * @returns {boolean}
 */
const voornaamGelijk = (x, y) =>
  !!x && !!y && (x.slice(0, 4) === y.slice(0, 4) || (x[0] === y[0] && gelijkenis(x, y) >= 0.8));

/**
 * #1957 — de voornaamhelft van `zelfdeNaam`: is de eerste voornaam van a en b dezelfde?
 * @param {string | null | undefined} a
 * @param {string | null | undefined} b
 * @returns {boolean}
 */
export const zelfdeVoornaam = (a, b) =>
  voornaamGelijk(naamSleutel(a).split(' ')[0], naamSleutel(b).split(' ')[0]);

/**
 * Zelfde persoonsnaam: eerste voornaam met hetzelfde begin, achternaam gelijkend.
 * Bewust zonder de variantentabel: die maakt Nicolaas en Klaas gelijk, en dan
 * ziet de vader van de Hoenkoopse naamgenoot van Jan Burger (geboren op
 * dezelfde dag) eruit als zijn eigen vader. Varianten alleen voor de eigen
 * voornaam, zie `voornaamKomtVoor` (src/utils/naamGelijkenis.ts).
 * @param {string} a
 * @param {string} b
 * @returns {boolean}
 */
export function zelfdeNaam(a, b) {
  const x = naamSleutel(a).split(' ');
  const y = naamSleutel(b).split(' ');
  if (!voornaamGelijk(x[0], y[0])) return false;
  const u = x[x.length - 1];
  const v = y[y.length - 1];
  // Steekproef 23-10: met 0,6 waren Boer, Bouwer en Burger dezelfde familie,
  // en Theodorus Bongers de moeder Theodora Lubbers. Arendse/Aarissen (0,62
  // met hetzelfde begin) blijft gelijk.
  // Vastgeschreven tussenvoegsel: "Maria Vanhees" op de akte is "Maria van Hees".
  /** @param {string} w */
  const los = (w) => w.replace(/^van(de[nr]?|der)?(?=[a-z]{3})/, '');
  if (los(u) === los(v)) return true;
  const g = gelijkenis(u, v);
  return (
    (u.slice(0, 2) === v.slice(0, 2) && g >= 0.5) ||
    g >= 0.75 ||
    (x[0] === y[0] && u[0] === v[0] && g >= 0.7)
  );
}

/** Patroniem-uitgangen, de langste eerst: Gerritsz, Gerritsen, Pieterszn, Hendrikse, Smits. */
const PATRONIEM_UITGANGEN = ['szn', 'sen', 'sz', 'se', 'zn', 's', 'z', 'x'];

/**
 * #1957 — de stam van een (patroniem)achternaam: kleine letters, zonder accenten en dubbele letters
 * (Janssen = Jansen), met één uitgang eraf als er minstens drie letters overblijven. Vóór 1811 werd
 * dezelfde man Gerritsz, Gerritsen en Gerrits genoemd; de klankregel alleen ziet dat niet.
 * @param {string} woord
 * @returns {string}
 */
function patroniemStam(woord) {
  const w = zonderAccent(woord)
    .toLowerCase()
    .replace(/[^a-z]/g, '')
    .replace(/(.)\1+/g, '$1');
  const uitgang = PATRONIEM_UITGANGEN.find((u) => w.endsWith(u) && w.length - u.length >= 3);
  return uitgang ? w.slice(0, -uitgang.length) : w;
}

/**
 * #1957 — de strenge regel voor koppelen en bevestigen: is dit dezelfde persoonsnaam?
 *
 * `zelfdeNaam` is de ruime lijstregel (chips) en vindt Woudstra = Woudenbergh, Boer = Bos, Jacobs =
 * Jansen: een andere man zou stil als dezelfde gelden. Daarom moet de achternaam er ook op klank
 * gelijk uit zien (`zelfdeKlank`, de regel van PeckArchive bij '~'), met of zonder tussenvoegsel
 * ("van Hees" = "Vanhees" = "Hees"). Het tussenvoegsel hoort bij de achternaam.
 * @param {{voornaam?: string | null, achternaam?: string | null}} a
 * @param {{voornaam?: string | null, achternaam?: string | null}} b
 * @returns {boolean}
 */
export function zelfdePersoonsnaam(a, b) {
  const [aa, ab] = [(a.achternaam || '').trim(), (b.achternaam || '').trim()];
  if (!aa || !ab) return false;
  const vol = (/** @type {{voornaam?: string | null}} */ x, /** @type {string} */ achter) =>
    `${x.voornaam || ''} ${achter}`.trim();
  if (!zelfdeNaam(vol(a, aa), vol(b, ab))) return false;
  /** @param {string} w */
  const kern = (w) => w.split(/\s+/).pop() || '';
  return (
    zelfdeKlank(aa, ab) ||
    zelfdeKlank(kern(aa), kern(ab)) ||
    (patroniemStam(kern(aa)) !== '' && patroniemStam(kern(aa)) === patroniemStam(kern(ab)))
  );
}

/** @param {string} s */
const zonderAccent = (s) => String(s).normalize('NFD').replace(/[̀-ͯ]/g, '');

/**
 * #1714 — de klankcode van PeckArchive (klank() uit migratie 015, "~ klinkt als"), regel voor regel
 * gespiegeld: wat PeckArchive op klank teruggeeft, moet de poort hieronder ook doorlaten. Wijzigt
 * de SQL-functie, dan deze mee (test: archiveNameMatching.klank1714).
 */
export function klank(naam) {
  let s = zonderAccent(String(naam || ''))
    .toLowerCase()
    .replace(/[^a-z]/g, '');
  s = s.replaceAll('sch', 's');
  s = s.replaceAll('ch', 'g');
  s = s.replaceAll('ph', 'f');
  s = s.replaceAll('th', 't');
  s = s.replaceAll('gh', 'g');
  s = s.replace(/c([eiy])/g, 's$1');
  s = s.replaceAll('ck', 'k').replaceAll('c', 'k').replaceAll('q', 'k');
  s = s.replaceAll('x', 'ks');
  s = s.replaceAll('z', 's');
  s = s.replaceAll('dt', 't').replace(/d$/, 't');
  s = s.replace(/uij|uy/g, 'ui');
  s = s.replace(/eij|eiy|ey|ei|ij|y/g, 'y');
  s = s.replace(/ouw|auw|ou|au/g, 'au');
  s = s.replaceAll('ae', 'a');
  s = s.replaceAll('v', 'f');
  s = s.slice(0, 1) + s.slice(1).replaceAll('h', '');
  return s.replace(/(.)\1+/g, '$1');
}

/**
 * #1714 — ie ≈ ei ≈ ij ≈ y, zoals PeckArchive bij "~": de klank van de naam zelf, van de vorm met ie→ij en van
 * die met ij/ei/y→ie (Riene/Reine, Ieperen/IJperen, Antonie/Anthonij).
 */
function klankVormen(naam) {
  const w = zonderAccent(String(naam || '')).toLowerCase();
  // Eén lettergreep: andere namen (Vries/Vrijs, Rijn/Rien) — zoals PeckArchive.
  if ((w.match(/[aeiouy]+/g) || []).length < 2) return new Set([klank(w)]);
  return new Set([klank(w), klank(w.replace(/ie/g, 'ij')), klank(w.replace(/ij|ei|y/g, 'ie'))]);
}
export const zelfdeKlank = (a, b) => {
  const va = klankVormen(a);
  return [...klankVormen(b)].some((k) => va.has(k));
};
